import React, { useState } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Video as VideoIcon,
  Trash2,
  Download,
  ExternalLink,
  Paperclip,
  Loader2,
  AlertCircle,
  Eye,
  X
} from 'lucide-react';
import MediaViewerModal from '../../Modals/MediaViewerModal';

import { getDownloadUrl } from 'service/project/api/media';
import { toast } from 'react-hot-toast';

export interface AttachmentDraft {
  id: string; // Internal local ID for UI management 
  mediaUuid?: string; // Real backend ID once uploaded
  name: string;
  size: number;
  type: string;
  url: string; // Local blob or real remote URL
  downloadUrl?: string; // Pre-fetched from backend
  previewUrl?: string; // Pre-fetched from backend
  file?: File;
  status: 'uploading' | 'ready' | 'error';
  errorMessage?: string;
  progress?: number;
}

interface AttachmentListProps {
  attachments: AttachmentDraft[];
  onRemove: (id: string) => void;
  disabled?: boolean;
  projectId?: string;
}

const AttachmentList: React.FC<AttachmentListProps> = ({ attachments, onRemove, disabled, projectId }) => {
  const [viewingFile, setViewingFile] = useState<AttachmentDraft | null>(null);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  // Always fetch fresh URLs on-demand for existing attachments when opened for viewing
  React.useEffect(() => {
    const fetchPreviewUrl = async () => {
      // For local unsaved files (blobs) that haven't been uploaded yet, skip fetching
      if (viewingFile && viewingFile.url.startsWith('blob:') && !viewingFile.mediaUuid) {
        return;
      }

      if (viewingFile && viewingFile.mediaUuid && projectId) {
        try {
          setIsFetchingUrl(true);
          const response = await getDownloadUrl(projectId, viewingFile.mediaUuid);
          if (response.url || response.previewUrl) {
            setViewingFile({ 
              ...viewingFile, 
              url: response.previewUrl || response.url,
              previewUrl: response.previewUrl,
              downloadUrl: response.downloadUrl
            });
          }
        } catch (error) {
          console.error("Failed to fetch preview URL:", error);
          toast.error("Failed to load preview");
        } finally {
          setIsFetchingUrl(false);
        }
      }
    };

    fetchPreviewUrl();
  }, [viewingFile?.id, projectId]);

  if (attachments.length === 0) return null;

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImage = (type: string) => type.startsWith('image/');
  const isPdf = (type: string) => type === 'application/pdf';
  const isVideo = (type: string) => type.startsWith('video/');

  const handleDownload = async (file: AttachmentDraft) => {
    // 1. Case for new unsaved files (blobs)
    if (!file.mediaUuid) {
      if (file.url.startsWith('blob:')) {
        const link = document.createElement('a');
        link.href = file.url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("Download not available yet");
      }
      return;
    }

    // 2. Refresh & use fresh download URL from backend on-demand
    if (!projectId) return;
    
    try {
      setIsDownloading(file.id);
      const response = await getDownloadUrl(projectId, file.mediaUuid);
      const url = response.downloadUrl || response.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        throw new Error("No download URL returned");
      }
    } catch (error: any) {
      console.error("Download error:", error);
      toast.error(error.message || "Failed to download file");
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {attachments.map((file) => (
          <div 
            key={file.id} 
            className={`group relative flex items-center gap-3 bg-primary-variant border rounded-lg p-3 transition-all duration-200 ${
              file.status === 'error' ? 'border-red-500/50' : 'border-primary-subtle hover:border-purple-500/50'
            }`}
          >
            {/* Clickable trigger area for preview */}
            <div 
              className={`flex-1 flex items-center gap-3 min-w-0 ${file.status === 'ready' ? 'cursor-pointer' : ''}`}
              onClick={() => file.status === 'ready' && setViewingFile(file)}
            >
              <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-primary-subtle rounded-md group-hover:bg-primary-subtle/80 transition-colors">
                {file.status === 'uploading' ? (
                  <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                ) : file.status === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : isImage(file.type) ? (
                  <ImageIcon className="w-5 h-5 text-teal-500" />
                ) : isVideo(file.type) ? (
                  <VideoIcon className="w-5 h-5 text-purple-400" />
                ) : (
                  <FileText className="w-5 h-5 text-blue-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-neutral truncate group-hover:text-purple-400 transition-colors" title={file.name}>
                  {file.name}
                </p>
                <p className="text-[10px] text-neutral-medium">
                  {file.status === 'uploading' ? 'Uploading...' : file.status === 'error' ? file.errorMessage || 'Failed' : formatSize(file.size)}
                </p>
              </div>
            </div>

            <div className={`flex items-center gap-1 transition-opacity`}>
              {file.status === 'ready' && (
                <>
                  <button 
                    onClick={() => setViewingFile(file)}
                    className="p-1.5 text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded transition-colors"
                    title="View"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button 
                    onClick={() => handleDownload(file)}
                    disabled={isDownloading === file.id}
                    className="p-1.5 text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded transition-colors disabled:opacity-50"
                    title="Download"
                  >
                    {isDownloading === file.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                  </button>
                </>
              )}
              <button
                onClick={() => onRemove(file.id)}
                disabled={disabled}
                className="p-1.5 text-neutral-medium hover:text-red-500 hover:bg-red-500/10 rounded transition-colors"
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <MediaViewerModal
        isOpen={!!viewingFile}
        onClose={() => setViewingFile(null)}
        file={viewingFile ? {
          name: viewingFile.name,
          type: viewingFile.type,
          url: viewingFile.url
        } : null}
        isFetchingUrl={isFetchingUrl}
        onDownload={viewingFile ? () => handleDownload(viewingFile) : undefined}
      />
    </div>
  );
};

export default AttachmentList;
